import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { RegisterFormData } from '../../types/auth';

/**
 * Registration Form Component
 * Handles new user registration with family code validation
 */
const RegisterForm: React.FC = () => {
  const { register, authState } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    firstName: '',
    lastName: '',
    familyCode: '',
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState<Partial<RegisterFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /**
   * Handle form field changes
   */
  const handleChange = (field: keyof RegisterFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'agreeToTerms' ? event.target.checked : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Auto-generate display name from first and last name
    if (field === 'firstName' || field === 'lastName') {
      const firstName = field === 'firstName' ? value : formData.firstName;
      const lastName = field === 'lastName' ? value : formData.lastName;
      
      if (firstName && lastName) {
        setFormData(prev => ({
          ...prev,
          displayName: `${firstName} ${lastName}`,
        }));
      }
    }

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  /**
   * Validate form fields
   */
  const validateForm = (): boolean => {
    const newErrors: Partial<RegisterFormData> = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    const minLength = parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '8');
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < minLength) {
      newErrors.password = `Password must be at least ${minLength} characters`;
    } else {
      // Additional password requirements
      if (process.env.REACT_APP_PASSWORD_REQUIRE_UPPERCASE === 'true' && !/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one uppercase letter';
      } else if (process.env.REACT_APP_PASSWORD_REQUIRE_LOWERCASE === 'true' && !/[a-z]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one lowercase letter';
      } else if (process.env.REACT_APP_PASSWORD_REQUIRE_NUMBERS === 'true' && !/\d/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one number';
      } else if (process.env.REACT_APP_PASSWORD_REQUIRE_SYMBOLS === 'true' && !/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one special character';
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    // Family code validation
    if (process.env.REACT_APP_REQUIRE_FAMILY_CODE === 'true' && !formData.familyCode?.trim()) {
      newErrors.familyCode = 'Family registration code is required';
    }

    // Terms agreement validation
    if (!formData.agreeToTerms) {
      (newErrors as any).agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await register(formData);
      
      // Redirect to success page or dashboard
      if (process.env.REACT_APP_ENABLE_EMAIL_VERIFICATION === 'true') {
        navigate('/auth/verify-email');
      } else if (process.env.REACT_APP_ENABLE_ADMIN_APPROVAL === 'true') {
        navigate('/auth/pending-approval');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      // Error is handled by the auth context
      console.error('Registration error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{ backgroundColor: 'background.default', px: 2, py: 4 }}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box textAlign="center" mb={3}>
            <Typography variant="h4" component="h1" gutterBottom>
              Join the Family Tree
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Request access to the Martz family genealogy
            </Typography>
          </Box>

          {/* Error Alert */}
          {authState.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {authState.error}
            </Alert>
          )}

          {/* Registration Form */}
          <Box component="form" onSubmit={handleSubmit}>
            {/* Name Fields */}
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName}
                margin="normal"
                autoComplete="given-name"
                disabled={isSubmitting}
              />

              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName}
                margin="normal"
                autoComplete="family-name"
                disabled={isSubmitting}
              />
            </Box>

            <TextField
              fullWidth
              label="Display Name"
              value={formData.displayName}
              onChange={handleChange('displayName')}
              error={!!errors.displayName}
              helperText={errors.displayName || 'This is how your name will appear to other family members'}
              margin="normal"
              autoComplete="name"
              disabled={isSubmitting}
            />

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
              autoComplete="email"
              disabled={isSubmitting}
            />

            {/* Password Fields */}
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange('password')}
              error={!!errors.password}
              helperText={errors.password}
              margin="normal"
              autoComplete="new-password"
              disabled={isSubmitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isSubmitting}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              margin="normal"
              autoComplete="new-password"
              disabled={isSubmitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      disabled={isSubmitting}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Family Code Field */}
            {process.env.REACT_APP_REQUIRE_FAMILY_CODE === 'true' && (
              <TextField
                fullWidth
                label="Family Registration Code"
                value={formData.familyCode}
                onChange={handleChange('familyCode')}
                error={!!errors.familyCode}
                helperText={errors.familyCode || 'Enter the code provided by a family member'}
                margin="normal"
                disabled={isSubmitting}
              />
            )}

            {/* Terms Agreement */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeToTerms}
                  onChange={handleChange('agreeToTerms')}
                  disabled={isSubmitting}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" style={{ color: 'inherit' }}>
                    Terms and Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" target="_blank" style={{ color: 'inherit' }}>
                    Privacy Policy
                  </Link>
                </Typography>
              }
              sx={{ mt: 2, mb: 2, alignItems: 'flex-start' }}
            />

            {errors.agreeToTerms && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {errors.agreeToTerms}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting}
              sx={{ mt: 2, mb: 2 }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Request Access'
              )}
            </Button>

            <Divider sx={{ my: 3 }} />

            <Box textAlign="center">
              <Typography variant="body2" color="textSecondary">
                Already have an account?{' '}
                <Typography
                  component={Link}
                  to="/login"
                  variant="body2"
                  color="primary"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Sign in
                </Typography>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Footer */}
      <Typography variant="body2" color="textSecondary" sx={{ mt: 4, textAlign: 'center' }}>
        {process.env.REACT_APP_SITE_NAME || 'Martz Family Genealogy'}
        <br />
        Private family genealogy website
      </Typography>
    </Box>
  );
};

export default RegisterForm;